import React from 'react';
import { createPortal } from 'react-dom';
import { ColorPickerComponent, type ColorPickerEventArgs } from '@syncfusion/ej2-react-inputs';
import * as Inovx from '@shared';
import UrlUtilities from '@shared/common/UrlUtilities';
import CustomHorizontalSplitter from '../../shared/components/controls/CustomHorizontalSplitter';
import ContextMenu from '../viewers/components/ContextMenu';
import type { AssignedAssetSetTaxonomy } from './AssignedAssetSetClassificationTree';
import FacilityAssetSetVirtualTree from './FacilityAssetSetVirtualTree';
import colorPickerPalette from './knowledgebook/colorPickerPalette';
import {
    buildAssetSetTreeIndex,
    projectTree,
    type FacilityAssetSetTreeNode,
} from '../helpers/facilityAssetSetTreeModel';
import {
    DISPLAY_COLOR_PALETTE,
    GOLDEN_DISPLAY_COLOR,
    addDisplayEntryAtTop,
    displayHexToNumber,
    getAssetSetDisplayAppearance,
    getTreeVisibilityState,
    normalizeDisplayID,
    numberToDisplayHex,
    removeDisplayEntry,
    reorderValidDisplayEntry,
    orderDisplayEntriesForPresentation,
    isDisplayConfigurationCleared,
    resetDisplayConfiguration,
    setDisplayEntryColor,
    setDisplayEntryMode,
    type TreeVisibilityState,
    isSpecialAssetSetType,
} from '../helpers/assetSetVisualizationModel';

const SystemActions = Inovx.Shared.System.Actions;

export type DisplayEntryPopulationStatus = 'ready' | 'pending' | 'error';

export interface DisplayColorRepository {
    getRecentColors(): readonly number[];
    commit(color: number): void;
    getSelectionSource(assetSetID: string, color: number): 'preset' | 'recent' | null;
    setSelectionSource(assetSetID: string, color: number, source: 'preset' | 'recent'): void;
}

export const createMemoryDisplayColorRepository = (): DisplayColorRepository => {
    let colors: number[] = [];
    const selections = new Map<string, { color: number; source: 'preset' | 'recent' }>();
    return {
        getRecentColors: () => colors,
        commit: color => {
            const normalized = color >>> 0;
            colors = [normalized, ...colors.filter(item => item !== normalized)].slice(0, 8);
        },
        getSelectionSource: (assetSetID, color) => {
            const selection = selections.get(normalizeDisplayID(assetSetID));
            return selection?.color === (color >>> 0) ? selection.source : null;
        },
        setSelectionSource: (assetSetID, color, source) => {
            selections.set(normalizeDisplayID(assetSetID), { color: color >>> 0, source });
        },
    };
};

export interface AssetSetDisplayPriorityEditorProps {
    configuration: IAssetSetDisplayConfiguration;
    onChange(configuration: IAssetSetDisplayConfiguration): void;
    assetSets: readonly IAssetSet[];
    treeAssetSets?: readonly IAssetSet[];
    invalidAssetSetIDs?: ReadonlySet<string>;
    assignedTaxonomies: readonly AssignedAssetSetTaxonomy[];
    canEdit: boolean;
    showRemoveAllDisplayPriorityAction: boolean;
    height: number;
    includeKnowledgeBookRoot: boolean;
    selectedKnowledgeBook?: IKnowledgeBook | null;
    knowledgeBookAssetSets?: readonly IAssetSet[];
    populationStatusByAssetSetID?: ReadonlyMap<string, DisplayEntryPopulationStatus>;
    onRetryPopulation?: (assetSetID: string) => void;
    colorRepository: DisplayColorRepository;
}

const EMPTY_INVALID_IDS: ReadonlySet<string> = new Set();
const SPLITTER_HANDLE_HEIGHT = 10;
const MIN_PANE_HEIGHT = 120;
const TOP_RATIO = 0.58;
const SEARCH_ROW_HEIGHT = 42;
const DISPLAY_MODES: readonly AssetSetDisplayMode[] = ['default', 'color', 'hidden'];

const cssColor = (color?: number | null) => numberToDisplayHex(color).slice(0, 7);

/** Hidden reads as an eye glyph; Default and Color reuse the shared square swatch. */
export const AssetSetDisplayModeGlyph = ({ mode, color }: { mode: AssetSetDisplayMode; color?: number | null }) => mode === 'hidden'
    ? <i className="fa fa-eye-slash asset-set-display-glyph-icon" aria-hidden="true" />
    : mode === 'color'
        ? <span className="asset-set-display-glyph is-color" style={{ backgroundColor: cssColor(color) }} aria-hidden="true" />
        : <span className="asset-set-display-glyph is-default" aria-hidden="true" />;

const ModeGlyph = AssetSetDisplayModeGlyph;

const AggregateGlyph = ({ state }: { state: TreeVisibilityState }) => state === 'invisible'
    ? <i className="fa fa-eye-slash asset-set-display-glyph-icon" aria-hidden="true" />
    : <span className={`asset-set-display-glyph is-${state}`} aria-hidden="true" />;

interface AppliedColorPickerProps {
    value: number;
    disabled: boolean;
    onApply(color: number): void;
}

const AppliedColorPicker = ({ value, disabled, onApply }: AppliedColorPickerProps) => {
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const popupRef = React.useRef<HTMLDivElement>(null);
    const [popupPosition, setPopupPosition] = React.useState<{ left: number; top: number } | null>(null);
    const [draftHex, setDraftHex] = React.useState(numberToDisplayHex(value));

    React.useEffect(() => {
        if (!popupPosition) setDraftHex(numberToDisplayHex(value));
    }, [popupPosition, value]);

    React.useEffect(() => {
        if (!popupPosition) return;

        const handleDocumentMouseDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (target && (popupRef.current?.contains(target) || triggerRef.current?.contains(target))) return;
            setPopupPosition(null);
        };
        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as Element | null;
            if (target?.closest('.asset-set-display-color-popup .e-cancel')) setPopupPosition(null);
        };
        document.addEventListener('mousedown', handleDocumentMouseDown);
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [popupPosition]);

    const open = () => {
        if (disabled) return;
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setDraftHex(numberToDisplayHex(value));
        setPopupPosition({
            left: Math.max(8, Math.min(rect.right - 290, window.innerWidth - 306)),
            top: Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - 420)),
        });
    };
    const selectDraft = (event: ColorPickerEventArgs) => {
        const next = event.currentValue?.hex;
        if (next) setDraftHex(next.padEnd(9, 'f'));
    };
    const apply = (event: ColorPickerEventArgs) => {
        const next = displayHexToNumber(event.value || draftHex) ?? GOLDEN_DISPLAY_COLOR;
        onApply(next);
        setPopupPosition(null);
    };

    return (
        <div className="asset-set-display-more-color">
            <button
                ref={triggerRef}
                type="button"
                className="btn btn-link"
                disabled={disabled}
                onClick={open}
            >
                {InovxLoader.t('facility:assetSetVisualization.more')}
            </button>
            {popupPosition && createPortal(
                <div
                    ref={popupRef}
                    className="asset-set-display-color-popup"
                    style={popupPosition}
                >
                    <ColorPickerComponent
                        value={numberToDisplayHex(value)}
                        inline
                        mode="Picker"
                        columns={10}
                        presetColors={colorPickerPalette}
                        showRecentColors={false}
                        showButtons
                        enableOpacity
                        modeSwitcher
                        select={selectDraft}
                        change={apply}
                    />
                </div>,
                document.body
            )}
        </div>
    );
};

export default function AssetSetDisplayPriorityEditor({
    configuration,
    onChange,
    assetSets,
    treeAssetSets,
    invalidAssetSetIDs = EMPTY_INVALID_IDS,
    assignedTaxonomies,
    canEdit,
    showRemoveAllDisplayPriorityAction,
    height,
    includeKnowledgeBookRoot,
    selectedKnowledgeBook,
    knowledgeBookAssetSets,
    populationStatusByAssetSetID,
    onRetryPopulation,
    colorRepository,
}: AssetSetDisplayPriorityEditorProps) {
    const invalidIDs = React.useMemo(() => new Set([...invalidAssetSetIDs].map(normalizeDisplayID)), [invalidAssetSetIDs]);
    const editPermissionsRef = React.useRef({ canEdit, invalidIDs });
    editPermissionsRef.current = { canEdit, invalidIDs };
    const canEditEntry = (id: string) => editPermissionsRef.current.canEdit &&
        !editPermissionsRef.current.invalidIDs.has(normalizeDisplayID(id));
    const [searchText, setSearchText] = React.useState('');
    const [selectedEntryID, setSelectedEntryID] = React.useState<string | null>(null);
    const [menu, setMenu] = React.useState<{ left: number; top: number; node: FacilityAssetSetTreeNode } | null>(null);
    const [entryMenu, setEntryMenu] = React.useState<{ left: number; top: number; assetSetID: string } | null>(null);
    const [displayPriorityMenu, setDisplayPriorityMenu] = React.useState<{ left: number; top: number } | null>(null);
    const [draggedEntryID, setDraggedEntryID] = React.useState<string | null>(null);
    const [dropTarget, setDropTarget] = React.useState<{ assetSetID: string; after: boolean } | null>(null);
    const [, setExpansionRevision] = React.useState(0);
    const [, setColorHistoryRevision] = React.useState(0);
    const expandedRowsRef = React.useRef<Set<string>>(new Set());
    const filteredExpandedRowsRef = React.useRef<Set<string>>(new Set());

    React.useEffect(() => {
        setSearchText('');
        setSelectedEntryID(null);
        setEntryMenu(null);
        setDisplayPriorityMenu(null);
        expandedRowsRef.current = new Set();
        filteredExpandedRowsRef.current = new Set();
    }, [configuration.FacilityID]);

    const facilitySets = React.useMemo(() => (treeAssetSets ?? assetSets).filter(set =>
        !set.KnowledgeBookID && !isSpecialAssetSetType(set.Type)), [assetSets, treeAssetSets]);
    const treeIndex = React.useMemo(() => buildAssetSetTreeIndex(
        assignedTaxonomies,
        facilitySets,
        {
            includeKnowledgeBookRoot,
            selectedKnowledgeBook,
            knowledgeBookAssetSets,
            knowledgeBookRootLabel: InovxLoader.t('facility:assetSetVisualization.knowledgeBookAssetSets'),
        }
    ), [assignedTaxonomies, facilitySets, includeKnowledgeBookRoot, knowledgeBookAssetSets, selectedKnowledgeBook]);
    const projection = React.useMemo(() => projectTree(treeIndex, {
        searchQuery: searchText,
        removeEmptyBranches: true,
    }), [searchText, treeIndex]);
    const listedIDs = React.useMemo(() => new Set(configuration.Entries.map(entry =>
        normalizeDisplayID(entry.AssetSetID))), [configuration]);
    const assetSetsByID = React.useMemo(() => new Map(assetSets.map(set =>
        [normalizeDisplayID(set.ID), set] as const)), [assetSets]);
    const fallbackAssetSetID = assetSetsByID.has(normalizeDisplayID(configuration.All3DAssetsID))
        ? configuration.All3DAssetsID
        : configuration.AllAssetTagsID;
    const visibleEntries = React.useMemo(() => orderDisplayEntriesForPresentation(
        configuration.Entries.filter(entry => assetSetsByID.has(normalizeDisplayID(entry.AssetSetID))), invalidIDs
    ), [assetSetsByID, configuration.Entries, invalidIDs]);

    React.useEffect(() => {
        if (selectedEntryID && invalidIDs.has(normalizeDisplayID(selectedEntryID))) setSelectedEntryID(null);
        if (draggedEntryID && invalidIDs.has(normalizeDisplayID(draggedEntryID))) setDraggedEntryID(null);
        if (dropTarget && invalidIDs.has(normalizeDisplayID(dropTarget.assetSetID))) setDropTarget(null);
    }, [invalidIDs, selectedEntryID, draggedEntryID, dropTarget]);
    const isDisplayPriorityCleared = isDisplayConfigurationCleared(configuration);

    const canShowMenu = React.useCallback((node: FacilityAssetSetTreeNode) =>
        canEdit && node.itemType === 'root' &&
        (node.source === 'facility' || node.source === 'knowledgeBook') && !!node.assetSetId &&
        !listedIDs.has(normalizeDisplayID(node.assetSetId)), [canEdit, listedIDs]);

    const firstColor = () => colorRepository.getRecentColors()[0] ?? GOLDEN_DISPLAY_COLOR;
    const presetColors = () => DISPLAY_COLOR_PALETTE.map(color => color >>> 0);
    const recentColors = () => colorRepository.getRecentColors().map(color => color >>> 0);
    const selectColor = (
        assetSetID: string,
        color: number,
        source: 'preset' | 'recent',
        addToRecent: boolean
    ) => {
        if (!canEditEntry(assetSetID)) return;
        if (addToRecent) colorRepository.commit(color);
        colorRepository.setSelectionSource(assetSetID, color, source);
        setColorHistoryRevision(revision => revision + 1);
        onChange(setDisplayEntryColor(configuration, assetSetID, color));
    };
    const addEntry = (node: FacilityAssetSetTreeNode, mode: AssetSetDisplayMode) => {
        if (!node.assetSetId || !canEditEntry(node.assetSetId) || listedIDs.has(normalizeDisplayID(node.assetSetId)) ||
            !treeIndex.occurrenceIdsByAssetSetId.has(node.assetSetId)) return;
        const initialColor = mode === 'color' ? firstColor() : undefined;
        onChange(addDisplayEntryAtTop(
            configuration,
            node.assetSetId,
            mode,
            initialColor
        ));
        setSelectedEntryID(node.assetSetId);
    };

    // Asset Set leaves report their own or inherited display mode; taxonomy rows report
    // the aggregate visibility of their unique descendant Asset Sets.
    const renderState = React.useCallback((node: FacilityAssetSetTreeNode) => {
        if (node.itemType === 'root' && node.assetSetId) {
            const appearance = getAssetSetDisplayAppearance(node.assetSetId, configuration, fallbackAssetSetID);
            const label = InovxLoader.t(`facility:assetSetVisualization.mode.${appearance.mode}`);
            return (
                <span className={`asset-set-display-state is-mode-${appearance.mode}`} title={label} aria-label={label}>
                    <ModeGlyph mode={appearance.mode} color={appearance.color} />
                </span>
            );
        }
        if (node.itemType !== 'tag') return null;
        const descendants = treeIndex.descendantAssetSetIdsByRowId.get(node.id) ?? [];
        if (!descendants.length) return null;
        const state = getTreeVisibilityState(descendants, configuration, fallbackAssetSetID);
        const label = InovxLoader.t(`facility:assetSetVisualization.state.${state}`);
        return (
            <span className={`asset-set-display-state is-${state}`} title={label} aria-label={label}>
                <AggregateGlyph state={state} />
            </span>
        );
    }, [configuration, fallbackAssetSetID, treeIndex]);

    const availablePaneHeight = Math.max(height - SPLITTER_HANDLE_HEIGHT, 0);
    const initialTreeHeight = Math.floor(availablePaneHeight * TOP_RATIO);

    return (
        <div className="asset-set-display-priority-editor" style={{ height }}>
            <CustomHorizontalSplitter
                className="kb-asset-sets-splitter asset-set-display-priority-splitter"
                containerHeight={height}
                initialPosition={initialTreeHeight}
                minTopHeight={MIN_PANE_HEIGHT}
                minBottomHeight={MIN_PANE_HEIGHT}
                showBottomPanel
                followInitialPositionUntilResized
                handleAriaLabel={InovxLoader.t('facility:assetSetVisualization.resizePanels')}
                topPanel={treeHeight => (
                    <div className="asset-set-display-tree-pane" style={{ height: treeHeight }}>
                        <div className="d-flex tag-search mt-4px mb-4px">
                            <div className="search-box w-100">
                                <div className="search-addon">
                                    <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/search_light.png`} />
                                </div>
                                <input
                                    value={searchText}
                                    onChange={event => setSearchText(event.target.value)}
                                    placeholder={InovxLoader.t('facility:knowledgeBook.searchByTagOrAssetSetName')}
                                    className="form-control"
                                />
                                <div className="search-addon">
                                    {searchText && (
                                        <button type="button" className="no-btn" title={InovxLoader.t('facility:assetTagFilter.clear')} onClick={() => setSearchText('')}>
                                            <img className="h-12px" src={`${UrlUtilities.getBaseApplicationUrl()}/images/tag/close.png`} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="asset-set-display-tree tree-list" style={{ height: Math.max(treeHeight - SEARCH_ROW_HEIGHT, 0) }}>
                            <FacilityAssetSetVirtualTree
                                index={treeIndex}
                                projection={projection}
                                isSearchActive={!!searchText.trim()}
                                expandedRowIdsRef={expandedRowsRef}
                                filteredExpandedRowIdsRef={filteredExpandedRowsRef}
                                selectedAssetSetId={null}
                                onActivate={() => undefined}
                                onExpansionChanged={() => setExpansionRevision(value => value + 1)}
                                onMenu={(event, node) => {
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    setMenu({ left: rect.right - 8, top: rect.bottom + 4, node });
                                }}
                                canShowMenu={canShowMenu}
                                renderHeader={() => (
                                    <div className="asset-set-display-column-header asset-set-display-tree-header">
                                        <span className="asset-set-display-column-name">{InovxLoader.t('facility:assetSetVisualization.assetSets')}</span>
                                        <span className="asset-set-display-column-state">{InovxLoader.t('facility:assetSetVisualization.state.title')}</span>
                                    </div>
                                )}
                                renderRowAccessory={renderState}
                                emptyMessage={InovxLoader.t('facility:assetSetFilter.noMatches')}
                                height={Math.max(treeHeight - SEARCH_ROW_HEIGHT, 0)}
                                width="100%"
                            />
                        </div>
                    </div>
                )}
                bottomPanel={bottomHeight => (
                    <div className="asset-set-display-priority-pane" style={{ height: bottomHeight }}>
                        <div className="asset-set-display-priority-title">
                            <span>{InovxLoader.t('facility:assetSetVisualization.displayPriority')}</span>
                            {canEdit && showRemoveAllDisplayPriorityAction && (
                                <button
                                    type="button"
                                    className="no-btn asset-set-display-priority-menu-trigger"
                                    title={InovxLoader.t('facility:grid.commands')}
                                    aria-label={InovxLoader.t('facility:grid.commands')}
                                    onClick={event => {
                                        const rect = event.currentTarget.getBoundingClientRect();
                                        setDisplayPriorityMenu({ left: rect.right - 8, top: rect.bottom + 4 });
                                    }}
                                >
                                    <i className="fa fa-ellipsis-v" aria-hidden="true" />
                                </button>
                            )}
                        </div>
                        <div className="asset-set-display-column-header asset-set-display-priority-header">
                            <span className="asset-set-display-column-name">{InovxLoader.t('facility:assetSetVisualization.assetSetColumn')}</span>
                            <span className="asset-set-display-column-mode">{InovxLoader.t('facility:assetSetVisualization.modeColumn')}</span>
                        </div>
                        <div className="asset-set-display-priority-list">
                            {visibleEntries.map(entry => {
                                const assetSet = assetSetsByID.get(normalizeDisplayID(entry.AssetSetID));
                                const key = normalizeDisplayID(entry.AssetSetID);
                                const isAll = key === normalizeDisplayID(configuration.All3DAssetsID)
                                    || key === normalizeDisplayID(configuration.AllAssetTagsID);
                                const invalid = invalidIDs.has(key);
                                const selected = !invalid && normalizeDisplayID(entry.AssetSetID) === normalizeDisplayID(selectedEntryID);
                                const status = populationStatusByAssetSetID?.get(normalizeDisplayID(entry.AssetSetID)) ?? 'ready';
                                const color = (entry.Color ?? firstColor()) >>> 0;
                                const name = assetSet?.Type === AssetSetType.All3DAssets
                                    ? InovxLoader.t('facility:assetSetVisualization.all3DAssets')
                                    : assetSet?.Type === AssetSetType.AllAssetTags
                                        ? InovxLoader.t('facility:assetSetVisualization.allAssetTags')
                                        : assetSet?.Name ?? entry.AssetSetID;
                                const toggleSelected = () => { if (!invalid) setSelectedEntryID(selected ? null : entry.AssetSetID); };
                                return (
                                    <div
                                        key={entry.AssetSetID}
                                        className={`asset-set-display-priority-row${selected ? ' is-selected' : ''}${invalid ? ' is-invalid' : ''}${isAll ? ' is-all-assets' : ''}${draggedEntryID === entry.AssetSetID ? ' is-dragging' : ''}${dropTarget?.assetSetID === entry.AssetSetID ? (dropTarget.after ? ' drop-after' : ' drop-before') : ''}`}
                                        draggable={canEdit && !isAll && !invalid}
                                        onDragStart={event => {
                                            if (!canEditEntry(entry.AssetSetID) || isAll) { event.preventDefault(); return; }
                                            event.dataTransfer.effectAllowed = 'move';
                                            event.dataTransfer.setData('text/plain', entry.AssetSetID);
                                            setDraggedEntryID(entry.AssetSetID);
                                            setDropTarget(null);
                                        }}
                                        onDragOver={event => {
                                            if (!canEditEntry(entry.AssetSetID) || !draggedEntryID || !canEditEntry(draggedEntryID) || isAll || draggedEntryID === entry.AssetSetID) {
                                                setDropTarget(null);
                                                return;
                                            }
                                            event.preventDefault();
                                            event.dataTransfer.dropEffect = 'move';
                                            const rect = event.currentTarget.getBoundingClientRect();
                                            const after = event.clientY >= rect.top + rect.height / 2;
                                            setDropTarget(current => current?.assetSetID === entry.AssetSetID && current.after === after
                                                ? current : { assetSetID: entry.AssetSetID, after });
                                        }}
                                        onDragLeave={event => {
                                            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropTarget(null);
                                        }}
                                        onDragEnd={() => { setDraggedEntryID(null); setDropTarget(null); }}
                                        onDrop={event => {
                                            event.preventDefault();
                                            if (canEditEntry(entry.AssetSetID) && draggedEntryID && canEditEntry(draggedEntryID) &&
                                                !isAll && dropTarget?.assetSetID === entry.AssetSetID) {
                                                const next = reorderValidDisplayEntry(configuration, draggedEntryID, entry.AssetSetID, dropTarget.after, invalidIDs);
                                                if (next !== configuration) onChange(next);
                                            }
                                            setDraggedEntryID(null);
                                            setDropTarget(null);
                                        }}
                                    >
                                        <div
                                            className="asset-set-display-priority-summary"
                                            role={invalid ? 'group' : 'button'}
                                            tabIndex={invalid ? -1 : 0}
                                            aria-expanded={invalid ? undefined : selected}
                                            onClick={toggleSelected}
                                            onKeyDown={event => {
                                                if (event.target !== event.currentTarget || invalid) return;
                                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                                event.preventDefault();
                                                toggleSelected();
                                            }}
                                        >
                                            <span className={`asset-set-display-drag-handle${isAll || invalid ? ' is-locked' : ''}`} aria-hidden="true">
                                                {isAll || invalid
                                                    ? <i className="fa fa-lock" />
                                                    : <><i className="fa fa-ellipsis-v" /><i className="fa fa-ellipsis-v" /></>}
                                            </span>
                                            <span className="asset-set-display-name-cell">
                                                <span className="display-name-text" title={name}>{name}</span>
                                                {!invalid && status === 'pending' && <i className="fa fa-spinner fa-spin asset-set-display-status" title={InovxLoader.t('facility:assetSetVisualization.loading')} />}
                                                {!invalid && status === 'error' && <i className="fa fa-exclamation-triangle asset-set-display-status is-error" title={InovxLoader.t('facility:assetSetVisualization.loadFailed')} />}
                                            </span>
                                            <span className="asset-set-display-mode-cell"
                                                title={invalid ? InovxLoader.t('facility:assetSetVisualization.panoInvalidReason') : undefined}>
                                                {!invalid && <ModeGlyph mode={entry.Mode} color={color} />}
                                                <span className="asset-set-display-mode-label">
                                                    {invalid ? InovxLoader.t('facility:assetSetVisualization.invalid')
                                                        : InovxLoader.t(`facility:assetSetVisualization.mode.${entry.Mode}`)}
                                                </span>
                                            </span>
                                            <span className="asset-set-display-priority-actions">
                                                {canEdit && !isAll && (
                                                    <button
                                                        type="button"
                                                        className="no-btn"
                                                        title={InovxLoader.t('facility:grid.commands')}
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            const rect = event.currentTarget.getBoundingClientRect();
                                                            setEntryMenu({ left: rect.right - 8, top: rect.bottom + 4, assetSetID: entry.AssetSetID });
                                                        }}
                                                    >
                                                        <i className="fa fa-ellipsis-v" />
                                                    </button>
                                                )}
                                            </span>
                                        </div>
                                        {selected && (
                                            <div className="asset-set-display-mode-editor">
                                                <div className="asset-set-display-mode-switch" role="group" aria-label={InovxLoader.t('facility:assetSetVisualization.modeColumn')}>
                                                    {DISPLAY_MODES.map(mode => (
                                                        <button
                                                            key={mode}
                                                            type="button"
                                                            className={`asset-set-display-mode-option${entry.Mode === mode ? ' is-active' : ''}`}
                                                            aria-pressed={entry.Mode === mode}
                                                            disabled={!canEdit}
                                                            onClick={() => canEditEntry(entry.AssetSetID) && onChange(setDisplayEntryMode(configuration, entry.AssetSetID, mode, firstColor()))}
                                                        >
                                                            {InovxLoader.t(`facility:assetSetVisualization.mode.${mode}`)}
                                                        </button>
                                                    ))}
                                                </div>
                                                {entry.Mode === 'color' && (
                                                    <div className="asset-set-display-color-controls">
                                                        <div className="asset-set-display-swatch-row is-preset">
                                                            {presetColors().map(swatch => (
                                                                <button
                                                                    type="button"
                                                                    key={swatch}
                                                                    className={`asset-set-display-swatch${color === swatch &&
                                                                        colorRepository.getSelectionSource(entry.AssetSetID, color) !== 'recent'
                                                                        ? ' is-selected' : ''}`}
                                                                    title={numberToDisplayHex(swatch)}
                                                                    style={{ backgroundColor: cssColor(swatch) }}
                                                                    disabled={!canEdit}
                                                                    onClick={() => selectColor(entry.AssetSetID, swatch, 'preset', false)}
                                                                />
                                                            ))}
                                                        </div>
                                                        <div className="asset-set-display-recent-controls">
                                                            <div className="asset-set-display-swatch-row is-recent">
                                                                {recentColors().map(swatch => (
                                                                    <button
                                                                        type="button"
                                                                        key={swatch}
                                                                        className={`asset-set-display-swatch${color === swatch &&
                                                                            colorRepository.getSelectionSource(entry.AssetSetID, color) === 'recent'
                                                                            ? ' is-selected' : ''}`}
                                                                        title={numberToDisplayHex(swatch)}
                                                                        style={{ backgroundColor: cssColor(swatch) }}
                                                                        disabled={!canEdit}
                                                                        onClick={() => selectColor(entry.AssetSetID, swatch, 'recent', true)}
                                                                    />
                                                                ))}
                                                            </div>
                                                            <AppliedColorPicker
                                                                value={color}
                                                                disabled={!canEdit}
                                                                onApply={next => selectColor(entry.AssetSetID, next, 'recent', true)}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {status === 'error' && onRetryPopulation && (
                                                    <button type="button" className="btn btn-link asset-set-display-retry" onClick={() => onRetryPopulation(entry.AssetSetID)}>
                                                        {InovxLoader.t('facility:assetSetVisualization.retry')}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            />
            {displayPriorityMenu && showRemoveAllDisplayPriorityAction && createPortal(
                <ContextMenu
                    left={displayPriorityMenu.left}
                    top={displayPriorityMenu.top}
                    items={[{
                        id: 'remove-all',
                        label: InovxLoader.t('facility:assetSetVisualization.removeAll'),
                        isDisabled: isDisplayPriorityCleared,
                    }]}
                    onItemClick={command => {
                        setDisplayPriorityMenu(null);
                        if (command !== 'remove-all' || !canEdit || isDisplayPriorityCleared) return;
                        SystemActions.displayConfirmationMessage({
                            message: InovxLoader.t('facility:assetSetVisualization.confirmResetVisualization'),
                            confirmCallback: () => {
                                setSelectedEntryID(null);
                                setEntryMenu(null);
                                onChange(resetDisplayConfiguration(configuration));
                            },
                        });
                    }}
                    onRequestClose={() => setDisplayPriorityMenu(null)}
                    containerId="display-priority-context-menu"
                    zIndex={110001}
                />,
                document.body
            )}
            {menu && createPortal(
                <ContextMenu
                    left={menu.left}
                    top={menu.top}
                    items={[
                        { id: 'hide', label: InovxLoader.t('facility:assetSetVisualization.hide') },
                        { id: 'colorize', label: InovxLoader.t('facility:assetSetVisualization.colorize') },
                        { id: 'default', label: InovxLoader.t('facility:assetSetVisualization.defaultAppearance') },
                    ]}
                    onItemClick={command => {
                        const current = menu.node;
                        setMenu(null);
                        if (command === 'default' || command === 'colorize' || command === 'hide') {
                            addEntry(current, command === 'default' ? 'default' : command === 'hide' ? 'hidden' : 'color');
                        }
                    }}
                    onRequestClose={() => setMenu(null)}
                    zIndex={110001}
                />,
                document.body
            )}
            {entryMenu && createPortal(
                <ContextMenu
                    left={entryMenu.left}
                    top={entryMenu.top}
                    items={[{ id: 'remove', label: InovxLoader.t('facility:assetSetVisualization.remove') }]}
                    onItemClick={command => {
                        const assetSetID = entryMenu.assetSetID;
                        setEntryMenu(null);
                        if (command !== 'remove' || !canEdit) return;
                        if (normalizeDisplayID(assetSetID) === normalizeDisplayID(selectedEntryID)) setSelectedEntryID(null);
                        onChange(removeDisplayEntry(configuration, assetSetID));
                    }}
                    onRequestClose={() => setEntryMenu(null)}
                    zIndex={110001}
                />,
                document.body
            )}
        </div>
    );
}
